import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { HomeService } from 'src/app/pages/home/service/home.service';
import { MenuService } from 'src/app/components/menu/service/menu.service';

export const DEFAULT_PLATFORM_HEADER_TITLE = 'Produtos ESG';
export const ESG_PLATFORM_HEADER_TITLE = 'Produtos ESG';

@Injectable({
  providedIn: 'root',
})
export class PlatformHeaderService implements OnDestroy {
  private readonly titleSubject = new BehaviorSubject<string>(DEFAULT_PLATFORM_HEADER_TITLE);
  private readonly subtitleSubject = new BehaviorSubject<string>('');
  private readonly showSubtitleSubject = new BehaviorSubject<boolean>(true);
  private dashboardSubscription?: Subscription;

  readonly title$ = this.titleSubject.asObservable();
  readonly subtitle$ = this.subtitleSubject.asObservable();
  readonly showSubtitle$ = this.showSubtitleSubject.asObservable();

  constructor(
    private readonly router: Router,
    private readonly homeService: HomeService,
    private readonly menuService: MenuService,
  ) {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
      )
      .subscribe(() => this.syncFromRoute());
  }

  ngOnDestroy(): void {
    this.dashboardSubscription?.unsubscribe();
  }

  shouldShowHeader(routePath: string): boolean {
    const hiddenRoutes = new Set(['oauth', 'acesso-negado']);
    const normalizedPath = routePath.replace(/^\//, '').split('?')[0].split('/')[0];
    return !hiddenRoutes.has(normalizedPath);
  }

  loadSubtitle(): void {
    this.ensureDashboardSubscription();

    const load = (): void => {
      if (this.homeService.hasDashboardData()) {
        return;
      }

      this.homeService.loadDashboard({
        showCreditStock: this.menuService.hasCreditStockPermission(),
        showRegisteredProjects: this.menuService.hasProjectConsultPermission(),
      }).subscribe();
    };

    if (this.menuService.isMenuLoaded()) {
      load();
      return;
    }

    this.menuService.whenMenuLoaded$().subscribe(() => load());
  }

  private ensureDashboardSubscription(): void {
    if (this.dashboardSubscription) {
      return;
    }

    this.dashboardSubscription = this.homeService.dashboard$.subscribe(dashboard => {
      if (dashboard?.greetingMessage) {
        this.subtitleSubject.next(dashboard.greetingMessage);
      }
    });
  }

  private syncFromRoute(): void {
    const data = this.getDeepestRouteData();

    this.titleSubject.next(DEFAULT_PLATFORM_HEADER_TITLE);
    this.showSubtitleSubject.next(data['headerShowSubtitle'] !== false);
  }

  private getDeepestRouteData(): Record<string, unknown> {
    let route = this.router.routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.snapshot.data ?? {};
  }
}
